import { uniqBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import {
  getBrowseOnlyLocalesForCountry,
  isBrowseOnlyCountry,
} from '@/core/constants/browse-only-resolution';
import {
  EXPANSION_MARKET_BROWSE_COUNTRIES,
  isExpansionMarketsOrderFlowEnabled,
} from '@/core/constants/expansion-markets';
import { CONFIGURED_NON_ECOMMERCE_MARKETS } from '@/core/constants/non-ecommerce-markets';
import { isSupportedLanguage } from '@/core/constants/supported-languages';
import useApplicationConfigurationQuery, {
  useFetchApplicationConfigurationQuery,
} from '@/core/queries/eva/use-application-configuration-query';
import useOrganizationsQuery from '@/core/queries/eva/use-organizations-query';
import useLocaleParams from '@/ui/hooks/use-locale-params';

export type CountryLanguageOption = {
  label: string;
  value: string;
};

type UseCountryLanguageOptionsArgs = {
  country: string;
  language: string;
};

const useCountryLanguageOptions = ({
  country: initialCountry,
  language: initialLanguage,
}: UseCountryLanguageOptionsArgs) => {
  const { language: urlLanguage, country: urlCountry } = useLocaleParams();
  const displayLanguage = urlLanguage || initialLanguage || 'en';
  const displayCountry = (urlCountry || initialCountry || 'nl').toUpperCase();
  const country = initialCountry.toUpperCase();

  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    country || null,
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    initialLanguage || null,
  );

  // The profile form mounts before the saved values reach it, so the incoming
  // country/language can still change after mount. Adopt them when they do,
  // otherwise the selection stays empty and no language options are fetched.
  const [syncedInput, setSyncedInput] = useState({
    country,
    language: initialLanguage,
  });
  if (
    syncedInput.country !== country ||
    syncedInput.language !== initialLanguage
  ) {
    setSyncedInput({ country, language: initialLanguage });
    if (country && country !== syncedInput.country) {
      setSelectedCountry(country);
    }
    if (initialLanguage && initialLanguage !== syncedInput.language) {
      setSelectedLanguage(initialLanguage);
    }
  }

  const { data: organizations } = useOrganizationsQuery();
  const evaCountryIds = useMemo(
    () => organizations?.map(org => org.countryId) ?? [],
    [organizations],
  );

  const isBrowseWithoutOrderFlowCountry = useMemo(
    () =>
      selectedCountry
        ? isBrowseOnlyCountry(selectedCountry, evaCountryIds)
        : false,
    [selectedCountry, evaCountryIds],
  );

  const selectedOrganization = organizations?.find(
    o => o.countryId === selectedCountry,
  );

  const { fetchQuery: fetchConfiguration } =
    useFetchApplicationConfigurationQuery();
  const { data: configuration } = useApplicationConfigurationQuery({
    organizationUnitId: selectedOrganization?.organizationId,
  });

  const handleSelectCountry = useCallback(
    async (newCountry: string): Promise<string> => {
      if (isBrowseOnlyCountry(newCountry, evaCountryIds)) {
        const supportedLocales = getBrowseOnlyLocalesForCountry(
          newCountry,
          evaCountryIds,
        ).filter(loc => isSupportedLanguage(loc.languageId));
        const newLanguage = supportedLocales.at(0)?.languageId ?? '';

        setSelectedCountry(newCountry);
        setSelectedLanguage(newLanguage);
        return newLanguage;
      }

      const newSelectedOrganization = organizations?.find(
        o => o.countryId === newCountry,
      );
      if (!newSelectedOrganization) {
        throw new Error('Organization not found');
      }

      const newConfiguration = await fetchConfiguration({
        organizationUnitId: newSelectedOrganization.organizationId,
      });

      const supportedCultures = newConfiguration.ContentCultures.filter(
        culture =>
          culture.CountryID === newCountry &&
          isSupportedLanguage(culture.LanguageID),
      );

      const orgUnitLanguage =
        newConfiguration.OrganizationUnitCulture?.LanguageID;
      const newLanguage =
        orgUnitLanguage && isSupportedLanguage(orgUnitLanguage)
          ? orgUnitLanguage
          : (supportedCultures.at(0)?.LanguageID ?? '');

      setSelectedCountry(newCountry);
      setSelectedLanguage(newLanguage);
      return newLanguage;
    },
    [evaCountryIds, fetchConfiguration, organizations],
  );

  const localeCountryDisplayNames = useMemo(
    () =>
      new Intl.DisplayNames(`${displayLanguage}-${displayCountry}`, {
        type: 'region',
      }),
    [displayLanguage, displayCountry],
  );
  const localeLanguageDisplayNames = useMemo(
    () =>
      new Intl.DisplayNames(`${displayLanguage}-${displayCountry}`, {
        type: 'language',
      }),
    [displayLanguage, displayCountry],
  );

  const evaCountriesOptions = uniqBy(organizations, 'countryId').map(org => ({
    label: localeCountryDisplayNames.of(org.countryId) ?? org.countryId,
    value: org.countryId,
  }));

  const nonEvaCountriesOptions = CONFIGURED_NON_ECOMMERCE_MARKETS.map(
    nonEcommerceMarket => ({
      label:
        localeCountryDisplayNames.of(nonEcommerceMarket.countryId) ??
        nonEcommerceMarket.countryName,
      value: nonEcommerceMarket.countryId,
    }),
  );

  const expansionBrowseCountriesOptions = useMemo(
    () =>
      isExpansionMarketsOrderFlowEnabled()
        ? []
        : EXPANSION_MARKET_BROWSE_COUNTRIES.map(expansionCountry => ({
            label:
              localeCountryDisplayNames.of(expansionCountry.countryId) ??
              expansionCountry.countryName,
            value: expansionCountry.countryId,
          })),
    [localeCountryDisplayNames],
  );

  const currentBrowseOnlyCountryOption = useMemo(() => {
    const listedCountry = selectedCountry || country;
    if (!listedCountry || !isBrowseOnlyCountry(listedCountry, evaCountryIds)) {
      return null;
    }
    const alreadyListed = [
      ...evaCountriesOptions,
      ...nonEvaCountriesOptions,
      ...expansionBrowseCountriesOptions,
    ].some(option => option.value === listedCountry);

    if (alreadyListed) {
      return null;
    }

    return {
      label: localeCountryDisplayNames.of(listedCountry) ?? listedCountry,
      value: listedCountry,
    };
  }, [
    country,
    selectedCountry,
    evaCountryIds,
    evaCountriesOptions,
    nonEvaCountriesOptions,
    expansionBrowseCountriesOptions,
    localeCountryDisplayNames,
  ]);

  const countriesOptions = uniqBy(
    [
      ...evaCountriesOptions,
      ...nonEvaCountriesOptions,
      ...expansionBrowseCountriesOptions,
      ...(currentBrowseOnlyCountryOption
        ? [currentBrowseOnlyCountryOption]
        : []),
    ],
    option => option.value.toUpperCase(),
  );

  const languagesOptions = useMemo(() => {
    if (isBrowseWithoutOrderFlowCountry) {
      return getBrowseOnlyLocalesForCountry(
        selectedCountry ?? '',
        evaCountryIds,
      )
        .filter(browseLocale => isSupportedLanguage(browseLocale.languageId))
        .map(browseLocale => ({
          label:
            localeLanguageDisplayNames.of(browseLocale.languageId) ??
            browseLocale.languageName,
          value: browseLocale.languageId,
        }));
    }

    return uniqBy(
      configuration?.ContentCultures.filter(
        culture =>
          culture.CountryID === selectedCountry &&
          isSupportedLanguage(culture.LanguageID),
      ),
      'LanguageID',
    ).map(culture => ({
      label:
        localeLanguageDisplayNames.of(culture.LanguageID) ?? culture.LanguageID,
      value: culture.LanguageID,
    }));
  }, [
    isBrowseWithoutOrderFlowCountry,
    selectedCountry,
    evaCountryIds,
    configuration,
    localeLanguageDisplayNames,
  ]);

  const hasSelectedAvailableCountry =
    !!selectedCountry &&
    countriesOptions.some(o => o.value === selectedCountry);
  const hasSelectedAvailableLanguage =
    !!selectedLanguage &&
    languagesOptions.some(o => o.value === selectedLanguage);

  const hasSelectedAvailable =
    hasSelectedAvailableCountry && hasSelectedAvailableLanguage;

  return {
    selectedCountry,
    selectedLanguage,
    setSelectedLanguage,
    countriesOptions,
    languagesOptions,
    handleSelectCountry,
    isBrowseWithoutOrderFlowCountry,
    selectedOrganization,
    evaCountryIds,
    hasSelectedAvailable,
  };
};

export default useCountryLanguageOptions;
